import {
  FormControlTypes,
  FormInputTypes,
  FormsControlConfig,
  FormSelectOption,
  FormValidatorConfig,
} from '../forms/forms.models';
import {
  GFFP_COUNTRY_CODES,
  GFFP_DEPENDENT_DROPDOWNS,
  GFFP_DROPDOWNS,
  GFFP_STATE_CODES,
  GffpFieldFormatted,
  GffpFieldPath,
  GffpFieldTypes,
  GffpFormats,
  GffpParties,
  GffpRef,
  GffpSchema,
  GffpSchemaConfig,
  GffpSchemas,
  isGffpSchema,
} from './gffp.models';
import gffpSchemasConfig from './gffp.schema.json';
import { EnumsFields } from '../store/enumeration/enumeration.model';
import { AppCountry, Labels } from '../app.models';
import { langSpecificEnums } from '../store/utils';

// TODO -- move to a service, maybe
export function mgGffpToFormControlConfig(
  fieldName: string,
  enums: EnumsFields[],
  labels: any,
  locale: string,
  countries: any,
  precaria: {
    flag: boolean;
    label: string;
    control: { name: string; optionValue: string };
  },
  defaultCountry?: string,
  isUpdateIdEnabled?: boolean
): FormsControlConfig | undefined {
  const allSchemas: GffpSchema = gffpSchemasConfig.schemas;

  // we get fields form API in two formats
  // <party>.<type>.<field>, ex: sender.primaryIdentification.issueCountry
  // <party>.<field>, ex: sender.birthCountry
  const fieldSegments = mgGffpPath(fieldName);
  const party = fieldSegments.party;
  const schemaPath = fieldSegments.schema;
  const fieldSegment = fieldSegments.field;

  // schema path needs capitalization to match json
  const schemaNameProperCase =
    (schemaPath && schemaPath.charAt(0).toUpperCase() + schemaPath.substr(1)) ||
    undefined;

  let schemaTop: GffpSchemaConfig;

  // <party>.<type>.<field>, we can go right to the schema
  if (schemaNameProperCase) {
    schemaTop = allSchemas[schemaNameProperCase] as GffpSchemaConfig;
    // <party>.<field> format so we have to look for the config
  } else {
    for (let schemaProp in allSchemas) {
      const schema: GffpSchemaConfig | GffpRef = allSchemas[schemaProp];

      if (
        isGffpSchema(schema) &&
        schema.properties &&
        schema.properties[fieldSegment]
      ) {
        schemaTop = schema as GffpSchemaConfig;
        break;
      }
    }
  }

  let neededSchema: GffpFieldFormatted;
  const propKey =
    (schemaTop! && schemaTop.properties && fieldSegment) || undefined;

  if (propKey) {
    const schemaOrRef: GffpSchemaConfig | GffpRef =
      schemaTop!.properties![propKey];

    if (schemaOrRef && isGffpSchema(schemaOrRef)) {
      const labelKey = `${party}${schemaNameProperCase || ''}${
        fieldSegment.charAt(0).toUpperCase() + fieldSegment.substr(1)
      }`;
      neededSchema = mgGffpFormatSchema(
        schemaOrRef,
        fieldName.replace(/\./g, '-'),
        (labels.stellar.gffp.gffpLabels[labelKey] as string) || labelKey,
        schemaTop!.required &&
          !!schemaTop!.required.find((name) => name === fieldSegment)
      );
    } else {
      // console.log('GFFP UTIL -- NOT HANDLED', schemaTop!, (schemaTop! && schemaTop.properties), fieldSegment)
    }
  }

  // dependent dropdowns, are formatted into the parent dropdown's config (`mgGffpGetSelectOptions`)
  if (
    neededSchema! &&
    !GFFP_DEPENDENT_DROPDOWNS.find(
      (dropDown) => dropDown.child === fieldSegment
    )
  ) {
    const controlType = mgGffpGetFormControlType(neededSchema, fieldSegment);
    const options =
      (controlType === FormControlTypes.SELECT &&
        mgGffpGetSelectOptions(
          fieldSegments,
          enums,
          labels,
          locale,
          countries,
          {
            ...precaria,
            flag:
              precaria.flag && precaria.control.name === neededSchema.name
                ? true
                : false,
          },
          defaultCountry,
          isUpdateIdEnabled
        )) ||
      undefined;
    return {
      name: neededSchema.name,
      label: neededSchema.label,
      controlType,
      inputType:
        (controlType === FormControlTypes.INPUT &&
          mgGffpGetFormInputType(neededSchema)) ||
        undefined,
      validators: mgGffpGetFormValidators(neededSchema, fieldSegments, labels),
      dependent: !!GFFP_DEPENDENT_DROPDOWNS.find(
        (dropDown) => dropDown.child === fieldSegment
      ),
      options,
    };
  }

  return undefined;
}

// we get fields form API in two formats
// <party>.<type>.<field>, ex: sender.primaryIdentification.issueCountry
// <party>.<field>, ex: sender.birthCountry
export function mgGffpPath(rawFieldName: string): GffpFieldPath {
  const split =
    rawFieldName.indexOf('.') > -1
      ? rawFieldName.split('.')
      : rawFieldName.split('-');

  const isPartyField: boolean = split.length === 2;

  return isPartyField
    ? {
        party: split[0] as GffpParties,
        field: split[1] as GffpSchemas,
      }
    : {
        party: split[0] as GffpParties,
        schema: split[1] as GffpSchemas,
        field: split[2],
      };
}

export function mgGffpFormatSchema(
  config: GffpSchemaConfig,
  name: string,
  label: string,
  required: boolean = false,
  items?: GffpFieldFormatted[]
): GffpFieldFormatted {
  return {
    ...config,
    type: config.type as GffpFieldTypes,
    format: config.format as GffpFormats,
    // form.get does not work with a `.`
    name,
    label,
    required,
    items,
  };
}

export function mgGffpGetFormControlType(
  config: GffpFieldFormatted,
  fieldSegment: string
): FormControlTypes {
  if (mgGffpIsDateField(config)) {
    return FormControlTypes.DATE;
  }

  // if we need to add more dropdowns, add to check
  if (mgGffpIsDropDown(fieldSegment)) {
    return FormControlTypes.SELECT;
  }

  return FormControlTypes.INPUT;
}

export function mgGffpIsDropDown(fieldSegment: string): boolean {
  return (
    GFFP_COUNTRY_CODES.indexOf(fieldSegment) > -1 ||
    GFFP_STATE_CODES.indexOf(fieldSegment) > -1 ||
    !!GFFP_DROPDOWNS.find((dropDown) => dropDown.name === fieldSegment)
  );
}

export function mgGffpGetFormInputType(
  config: GffpFieldFormatted
): FormInputTypes | undefined {
  if (config.type === GffpFieldTypes.NUMBER) {
    return 'number';
  } else if (config.type === GffpFieldTypes.STRING) {
    return 'text';
  } else {
    return undefined;
  }
}

export function mgGffpIsDateField(config: GffpFieldFormatted): boolean {
  return (
    config.format === GffpFormats.DATE ||
    config.format === GffpFormats.DATE_MDAY ||
    config.format === GffpFormats.DATE_MONTH ||
    config.format === GffpFormats.FULL_YEAR
  );
}

export function mgGffpGetFormValidators(
  gffpConfig: GffpFieldFormatted,
  path: GffpFieldPath,
  labels: any
): FormValidatorConfig[] {
  const validators: FormValidatorConfig[] = [];
  const isSecondaryId =
    path.schema === GffpSchemas.SECONDARY_ID && path.field === 'id';
  const isPrimaryId =
    path.schema === GffpSchemas.PRIMARY_ID && path.field === 'id';

  if (gffpConfig.maxLength || isSecondaryId) {
    // assuming SSN
    const maxLength = isSecondaryId ? 9 : gffpConfig.maxLength;

    validators.push({
      type: 'max',
      message: `${gffpConfig.label} ${labels.stellar.gffp.validations.longerChar} ${maxLength} ${labels.stellar.gffp.validations.characters}`,
      arg: maxLength,
    });
  }

  if (gffpConfig.minLength) {
    validators.push({
      type: 'min',
      message: `${gffpConfig.label} ${labels.stellar.gffp.validations.mustLongChar} ${gffpConfig.minLength} ${labels.stellar.gffp.validations.characters}`,
      arg: gffpConfig.minLength,
    });
  }

  if (gffpConfig.required) {
    validators.push({
      type: 'required',
      message: `${gffpConfig.label} ${labels.stellar.gffp.validations.required}`,
    });
  }

  if (gffpConfig.pattern || isPrimaryId || isSecondaryId) {
    // no pattern given in schema from MAPI for primary/secondary ID, overriding here.
    const pattern =
      (isSecondaryId && '^[0-9]+$') ||
      (isPrimaryId && '^[a-zA-Z0-9-]*$') ||
      gffpConfig.pattern!;

    validators.push({
      type: 'pattern',
      message: `${gffpConfig.label} ${labels.stellar.gffp.validations.invalid}`,
      arg: new RegExp(pattern),
    });
  }

  return validators;
}



export function mgGffpGetSelectOptions(
  path: GffpFieldPath,
  enums: EnumsFields[],
  labels: Labels,
  locale: string,
  countries: any,
  precaria: {
    flag: boolean;
    label: string;
    control: { name: string; optionValue: string };
  },
  defaultCountry?: string,
  isUpdateIdEnabled?: boolean
): FormSelectOption[] | undefined {
  if (GFFP_COUNTRY_CODES.indexOf(path.field) > -1) {
    const stateField = GFFP_DEPENDENT_DROPDOWNS.find(
      (dropDown) => dropDown.parent === path.field
    );
    const schemaProperCase =
      (path.schema &&
        `${path.schema.charAt(0).toUpperCase() + path.schema.substr(1)}`) ||
      undefined;
    const labelPrefix = `${path.party}${schemaProperCase || ''}`;
    const stateLabelKey =
      stateField &&
      `${labelPrefix}${
        stateField!.child.charAt(0).toUpperCase() + stateField!.child.substr(1)
      }`;
    const lang = locale?.substring(0, 2) || 'en';
    let countryConfigs: AppCountry[] = countries;
    const options =
      countryConfigs.map((countryConfig) => ({
        label: countryConfig.name,
        value: countryConfig.code,
        options:
          stateField &&
          countryConfig?.states &&
          countryConfig?.states?.map((state) => ({
            label: state.name,
            value: state.code,
          })),
        childControlName:
          stateField && `${path.party}-${path.schema}-${stateField.child}`,
        childLabel:
          (stateLabelKey &&
            (labels.stellar.gffp.gffpLabels[stateLabelKey] as string)) ||
          stateLabelKey,
      })) || undefined;

    return options;
    // ignore states, filled in by country above
  } else if (GFFP_STATE_CODES.indexOf(path.field) === -1) {
    const dropDown = GFFP_DROPDOWNS.find(
      (dropDown) =>
        dropDown.name === path.field &&
        (!dropDown.parent || dropDown.parent === path.schema)
    );
    const enumFields: EnumsFields[] = langSpecificEnums(
      enums,
      labels?.stellar?.enumsObj
    );

    return (
      (dropDown &&
        enumFields &&
        enumFields
          .find((item) => item.name === dropDown.enum)!
          .items// special filtering depending on schema
          // MAPI provided all possible enums, but we are limiting for secondaryId types per App requirements
          // For primaryId types, only allow GOV and PAS for non USA countries
          .filter(
            (item) =>
              (path.schema === GffpSchemas.SECONDARY_ID &&
                item.value === 'SSN') ||
              (path.schema === GffpSchemas.PRIMARY_ID &&
                isUpdateIdEnabled &&
                defaultCountry === 'ARG' &&
                (item.value === 'GOV' || item.value === 'PAS' || item.value === 'ALN')) ||
              (path.schema === GffpSchemas.PRIMARY_ID &&
                isUpdateIdEnabled &&
                defaultCountry &&
                (defaultCountry !== 'USA' && defaultCountry !== 'ARG') &&
                (item.value === 'GOV' || item.value === 'PAS')) ||
              (path.schema !== GffpSchemas.SECONDARY_ID && 
                path.schema !== GffpSchemas.PRIMARY_ID) ||
              (path.schema === GffpSchemas.PRIMARY_ID && 
                (!isUpdateIdEnabled || !defaultCountry || (defaultCountry === 'USA' || defaultCountry === 'CAN')))
          )
          .map((item) => {
            let label = item.description;
            
            // For GOV item, prepend country name if available
            if (item.value === 'GOV' && defaultCountry && countries && isUpdateIdEnabled) {
              const country = countries.find((c: any) => c.code === defaultCountry);
              if (country) {
                label = `${country.name} ${item.description}`;
              }
            }
            
            return {
              label: precaria.flag && item.value === precaria.control.optionValue
                ? precaria.label
                : label,
              value: item.value,
            };
          })) ||
      undefined
    );
  }

  return undefined;
}
